'use client'
import { Box, Flex, Spoiler, Text } from '@mantine/core'
import { useLocale, useTranslations } from 'next-intl'
import { useState } from 'react'
import sanitizeHtml from 'sanitize-html'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import ChevronDown from '@/assets/icons/chevron-down.svg'
import ChevronUp from '@/assets/icons/chevron-up.svg'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { useSsrContext } from '@/contexts/ssr'
import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'
import { useCoinStatistics } from '@/services/coin/market'
import { useCoinLocaleDesc } from '@/services/coin/meta'
import { useSuspenseCoinPricesBySlug } from '@/services/coin/prices'
import { formatMarketCap, formatPercentageColor, formatPrice } from '@/utils/number/format'

import styles from './style.module.css'

interface DescriptionProps {
  customHeader?: React.ReactNode
  isShowDescription?: boolean
}

function Description({ customHeader, isShowDescription = true }: DescriptionProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { coin } = useCoinContext()

  const circulatingSupply = coin.circulating_supply
  const maxSupply = coin.max_supply
  const t = useTranslations('about')
  const locale = useLocale()

  const { currency, amounts } = useCurrencyAmounts({
    amounts: [{ value: coin?.usd_price }, { value: coin?.usd_volume_24h }],
    tokenSymbol: coin?.symbol,
    isUsedForTokenPrice: true,
    isExchangeRateBasedOnSymbolCurrency: false,
  })
  const coinVolume = amounts?.[1]?.value
  const coinCurrencyPrice = amounts?.[0]?.value || coin?.usd_price || 0

  const { selectedCurrency } = useSsrContext()

  const currentCoinPrices = useSuspenseCoinPricesBySlug({
    slug: coin.slug,
    interval: 'd',
    convert: selectedCurrency,
  })

  const { data: statisticsData } = useCoinStatistics(coin.slug)
  const coin7dHigh = statisticsData?.statistics?.find(
    (item: { period: string; high: number }) => item.period === '7d',
  )?.high
  const coin7dLow = statisticsData?.statistics?.find(
    (item: { period: string; low: number }) => item.period === '7d',
  )?.low
  const isSameCryptocurrency = coin.symbol === currency.code
  const maxMin7d =
    !coin7dLow || !coin7dHigh
      ? undefined
      : {
          max7d: coin7dHigh,
          min7d: coin7dLow,
          max7dChange: isSameCryptocurrency
            ? 0
            : // use current currency price to get priceChange
              ((coinCurrencyPrice - coin7dHigh) / coin7dHigh) * 100,
          min7dChange: isSameCryptocurrency
            ? 0
            : ((coinCurrencyPrice - coin7dLow) / coin7dLow) * 100,
        }

  const coinName = coin?.name

  // Get localized description based on current locale
  let description = coin?.description

  // Always call the hook, but conditionally use the result
  const { data: localizedDesc } = useCoinLocaleDesc(coin?.id || 0, locale, true)

  if (locale !== 'en' && localizedDesc) {
    description = localizedDesc
  }
  const renderPriceChange = (value: number, showPositiveSymbol = true) => {
    const changeSymbol = Number(value) > 0 && showPositiveSymbol ? '+' : ''
    return `${changeSymbol}${value.toFixed(2)}%`
  }

  const renderColoredText = (value: number) => {
    const ColoredTextComponent = (chunks: React.ReactNode) => (
      <Text
        component="span"
        c={formatPercentageColor(value ?? 0)}
        fw={500}
        style={{
          fontWeight: 'inherit',
          lineHeight: 'inherit',
          fontSize: 'inherit',
        }}
      >
        {chunks}
      </Text>
    )
    return ColoredTextComponent
  }

  // Use sanitize-html to sanitize HTML content
  const sanitizedDescription = description
    ? sanitizeHtml(description, {
        allowedTags: [
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'p',
          'br',
          'strong',
          'em',
          'ul',
          'ol',
          'li',
          'a',
        ],
        allowedAttributes: {
          a: ['href', 'target'],
        },
        allowedSchemes: ['http', 'https'],
      })
    : ''

  return (
    <Box pb={0}>
      {/* Title area */}
      {customHeader}
      {!customHeader && (
        <>
          {' '}
          <Flex justify="space-between" align="center" mb={16}>
            <Text
              component="h2"
              variant="heading2"
              style={{ color: 'var(--text-primary, #FFFFFF)' }}
            >
              {t('title', { coinName })}
            </Text>
          </Flex>
          <Text variant="heading3" component="h3">
            {coinName} {t('description.priceSummaries')}
          </Text>
        </>
      )}
      <Text
        component="span"
        style={{
          color: 'var(--text-secondary)',
          display: 'inline',
          fontWeight: 500,
          fontSize: '16px',
          lineHeight: '20px',
        }}
      >
        <Text
          component="span"
          style={{
            display: 'inline',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            fontSize: 'inherit',
          }}
        >
          {t.rich('description.priceDescription', {
            coinName: coinName,
            price: `${currency.symbol}${formatPrice(coinCurrencyPrice)}`,
            volume: `${currency.symbol}${formatMarketCap(Number(coinVolume) || 0)}`,
            symbol: coin?.symbol,
            priceChange: renderColoredText(currentCoinPrices?.price_change ?? 0),
            priceChangeValue: renderPriceChange(
              Number((currentCoinPrices?.price_change ?? 0) * 100),
            ),
          })}
        </Text>
        {maxMin7d && (
          <Text
            component="span"
            style={{
              display: 'inline',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
              fontSize: 'inherit',
            }}
          >
            {t.rich('description.max7d', {
              changePercent: renderColoredText(maxMin7d.max7dChange),
              changePercentValue: renderPriceChange(Number(maxMin7d.max7dChange), false),
              high7d: `${currency.symbol}${formatPrice(maxMin7d.max7d)}`,
            })}
            {t.rich('description.min7d', {
              changePercent: renderColoredText(maxMin7d.min7dChange),
              changePercentValue: renderPriceChange(Number(maxMin7d.min7dChange), false),
              low7d: `${currency.symbol}${formatPrice(maxMin7d.min7d)}`,
            })}
          </Text>
        )}

        {circulatingSupply && maxSupply && (
          <Text
            component="span"
            style={{
              display: 'inline',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
              fontSize: 'inherit',
            }}
          >
            {t.rich('description.circulatingAndMax', {
              circulatingSupply: formatMarketCap(circulatingSupply),
              symbol: coin?.symbol,
              maxSupply: formatMarketCap(maxSupply),
            })}
          </Text>
        )}
        {circulatingSupply && !maxSupply && (
          <Text
            component="span"
            style={{
              display: 'inline',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
              fontSize: 'inherit',
            }}
          >
            {t.rich('description.onlyCirculating', {
              circulatingSupply: formatMarketCap(circulatingSupply),
              symbol: coin?.symbol,
            })}
          </Text>
        )}
        {!circulatingSupply && maxSupply && (
          <Text
            component="span"
            style={{
              display: 'inline',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
              fontSize: 'inherit',
            }}
          >
            {t.rich('description.onlyMax', {
              maxSupply: formatMarketCap(maxSupply),
              symbol: coin?.symbol,
            })}
          </Text>
        )}
      </Text>
      {/* Content area with Spoiler */}
      {isShowDescription && description?.length > 0 && (
        <Box pos="relative" mt={16}>
          <Spoiler
            maxHeight={140}
            transitionDuration={300}
            showLabel={
              <Flex gap={8} align="center">
                {t('description.showMore')}
                <ChevronDown
                  width={16}
                  height={16}
                  fill="var(--content-active, --common-primary)"
                />
              </Flex>
            }
            hideLabel={
              <Flex gap={8} align="center">
                {t('description.showLess')}
                <ChevronUp width={16} height={16} fill="var(--content-active, --common-primary)" />
              </Flex>
            }
            expanded={isExpanded}
            onExpandedChange={setIsExpanded}
            classNames={{
              control: styles.spoilerControl,
            }}
          >
            <Box
              className={styles.content}
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
            />
          </Spoiler>
          {/* Gradient overlay - positioned at the bottom of content area */}
          {!isExpanded && <Box className={styles.spoilerShadow} />}
        </Box>
      )}
    </Box>
  )
}

export default SectionErrorBoundary.with(Description)
